import { Trans, useLingui } from "@lingui/react/macro";
import * as Popover from "@radix-ui/react-popover";
import moment from "moment";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import type {
  ClinicalStudies,
  VisitCalendarEvent,
} from "../../api/generated/types.gen";

import { apiGetClinicalStudiesCalendar } from "../../api/generated/sdk.gen";
import { PopoverContent } from "../../components/ui-kit/popover";
import { useFetch } from "../../utils/use-fetch";
import styles from "./calendar-page.module.css";

interface CalendarEvent {
  id: string;
  title: string;
  date: moment.Moment;
  color?: string;
  patientId: number;
  study: ClinicalStudies;
}

// Color palette for different studies
const STUDY_COLORS: Record<string, string> = {
  AUGMENT: "#3b82f6", // blue
  CADENCE: "#ef4444", // red
  PULSE: "#10b981", // green
  RHYTHM: "#f59e0b", // amber
  TEMPO: "#8b5cf6", // violet
  HARMONY: "#06b6d4", // cyan
  MELODY: "#84cc16", // lime
  SYMPHONY: "#ec4899", // pink
  DEFAULT: "#6b7280", // gray
};

export function CalendarRoute(props: {
  weekStartDay?: 0 | 1; // 0 = Sunday, 1 = Monday
  maxEventsPerDay?: number; // Maximum events to show before "X more"
}) {
  const { t } = useLingui();
  const [currentDate, setCurrentDate] = useState(moment());
  const [events, setEvents] = useState<CalendarEvent[] | null>(null);

  const weekStartDay = props.weekStartDay ?? 1; // Default to Monday
  const maxEventsPerDay = props.maxEventsPerDay ?? 3; // Default to 3 events

  const { loading } = useFetch(async () => {
    const response = await apiGetClinicalStudiesCalendar({
      meta: { useSessionCache: true },
    });

    const calendarEvents: CalendarEvent[] = response.data.events.map(
      (event: VisitCalendarEvent) => ({
        id: `${event.patient_id}-${event.study_acronym}-${event.visit_type}`,
        title: event.pretty_event_name,
        date: moment(event.event_datetime),
        color: STUDY_COLORS[event.study_acronym] || STUDY_COLORS.DEFAULT,
        patientId: event.patient_id,
        study: event.study_acronym,
      }),
    );
    setEvents(calendarEvents);
  }, []);

  const calendarDays = useMemo(() => {
    const startOfMonth = currentDate.clone().startOf("month");
    const endOfMonth = currentDate.clone().endOf("month");

    // Get the start of the week for the first day of the month
    const firstDayOfMonth = startOfMonth.clone();
    const startOfWeek = firstDayOfMonth.clone();

    // Adjust to the correct week start day
    const currentWeekDay = startOfWeek.day(); // 0 = Sunday, 1 = Monday, etc.
    const targetWeekStart = weekStartDay;
    const daysToSubtract = (currentWeekDay - targetWeekStart + 7) % 7;
    startOfWeek.subtract(daysToSubtract, "days");

    // Get the end of the week for the last day of the month
    const lastDayOfMonth = endOfMonth.clone();
    const endOfWeek = lastDayOfMonth.clone();
    const lastWeekDay = endOfWeek.day();
    const daysToAdd = (targetWeekStart + 6 - lastWeekDay + 7) % 7;
    endOfWeek.add(daysToAdd, "days");

    const days = [];
    const current = startOfWeek.clone();

    while (current.isSameOrBefore(endOfWeek, "day")) {
      days.push(current.clone());
      current.add(1, "day");
    }

    return days;
  }, [currentDate, weekStartDay]);

  const getEventsForDay = (day: moment.Moment) => {
    return (events || []).filter((event) => event.date.isSame(day, "day"));
  };

  const getDisplayEventsForDay = (day: moment.Moment) => {
    const dayEvents = getEventsForDay(day);

    if (dayEvents.length <= maxEventsPerDay) {
      return { visibleEvents: dayEvents, hiddenCount: 0 };
    }

    return {
      visibleEvents: dayEvents.slice(0, maxEventsPerDay),
      hiddenCount: dayEvents.length - maxEventsPerDay,
    };
  };

  const goToPreviousMonth = () => {
    setCurrentDate(currentDate.clone().subtract(1, "month"));
  };

  const goToNextMonth = () => {
    setCurrentDate(currentDate.clone().add(1, "month"));
  };

  const goToToday = () => {
    setCurrentDate(moment());
  };

  const isToday = (day: moment.Moment) => {
    return day.isSame(moment(), "day");
  };

  const isCurrentMonth = (day: moment.Moment) => {
    return day.isSame(currentDate, "month");
  };

  const weekDays = useMemo(() => {
    const days = [t`Sun`, t`Mon`, t`Tue`, t`Wed`, t`Thu`, t`Fri`, t`Sat`];
    const startDay = weekStartDay;
    return [...days.slice(startDay), ...days.slice(0, startDay)];
  }, [weekStartDay]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.navigationControls}>
            <button
              className={styles.navButton}
              onClick={goToPreviousMonth}
              aria-label={t`Previous month`}
            >
              ❮
            </button>
            <button className={styles.todayButton} onClick={goToToday}>
              <Trans>Today</Trans>
            </button>
            <button
              className={styles.navButton}
              onClick={goToNextMonth}
              aria-label={t`Next month`}
            >
              ❯
            </button>
          </div>
          <h1 className={styles.monthTitle}>
            {currentDate.format("MMMM YYYY")}
          </h1>
        </div>

        {loading && (
          <div className="p-8 text-center">
            <Trans>Loading calendar events...</Trans>
          </div>
        )}

        {!loading && (
          <div className={styles.calendar}>
            <div className={styles.weekDaysHeader}>
              {weekDays.map((day) => (
                <div key={day} className={styles.weekDay}>
                  {day}
                </div>
              ))}
            </div>

            <div className={styles.calendarGrid}>
              {calendarDays.map((day) => {
                const { visibleEvents, hiddenCount } =
                  getDisplayEventsForDay(day);
                return (
                  <div
                    key={day.format("YYYY-MM-DD")}
                    className={`${styles.calendarDay} ${
                      isToday(day) ? styles.today : ""
                    } ${!isCurrentMonth(day) ? styles.otherMonth : ""}`}
                  >
                    <div className={styles.dayNumber}>{day.format("D")}</div>
                    <div className={styles.dayEvents}>
                      {visibleEvents.map((event) => (
                        <Link
                          key={event.id}
                          to={`/patient/${event.patientId}`}
                          className={styles.event}
                          style={{
                            backgroundColor: event.color || "#3b82f6",
                          }}
                          title={t`${event.title} - Patient #${event.patientId}`}
                        >
                          {event.title}
                        </Link>
                      ))}
                      {hiddenCount > 0 && (
                        <Popover.Root>
                          <Popover.Trigger className={styles.moreEvents}>
                            <Trans>+{hiddenCount} more</Trans>
                          </Popover.Trigger>
                          <Popover.Portal>
                            <PopoverContent>
                              <div className={styles.popoverHeader}>
                                <strong>{day.format("MMMM D, YYYY")}</strong>
                                <div className={styles.popoverSubtitle}>
                                  <Trans>
                                    {getEventsForDay(day).length} events
                                  </Trans>
                                </div>
                              </div>
                              <div className={styles.popoverEvents}>
                                {getEventsForDay(day).map((event) => (
                                  <Link
                                    key={event.id}
                                    to={`/patient/${event.patientId}`}
                                    className={styles.popoverEvent}
                                    style={{
                                      borderLeftColor: event.color || "#3b82f6",
                                    }}
                                  >
                                    <div className={styles.popoverEventTitle}>
                                      {event.title}
                                    </div>
                                    <div className={styles.popoverEventPatient}>
                                      <Trans>Patient #{event.patientId}</Trans>
                                    </div>
                                  </Link>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover.Portal>
                        </Popover.Root>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
